// src/components/tip_bon_iesire/TipBonIesireCreate.jsx
import React, { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import { AsyncPaginate } from "react-select-async-paginate";
import { components } from "react-select";
import { authFetch } from "../utils/authFetch"; // same-origin
import AccountPickerModal from "../pickers/AccountPickerModal";
const LIMIT = 20;

const HighlightedOption = (props) => {
  const q = (props.selectProps.inputValue || "").trim();
  const label = props.data.label || "";
  const parts = q ? label.split(new RegExp(`(${q})`, "i")) : [label];
  return (
    <components.Option {...props}>
      {parts.map((p, i) =>
        q && p.toLowerCase() === q.toLowerCase() ? (
          <mark key={i}>{p}</mark>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </components.Option>
  );
};

function useLoader(resource, makeLabel) {
  return useCallback(
    async (inputValue, _loaded, additional) => {
      const page = additional?.page ?? 1;
      const q = (inputValue || "").trim();
      const skip = (page - 1) * LIMIT;

      try {
        // 1) dacă NU cauți nimic -> paginare normală din backend
        if (!q) {
          const params = new URLSearchParams({
            skip: String(skip),
            limit: String(LIMIT),
            sort_by: "code",
            sort_order: "asc",
          });
          const data = await authFetch(`/${resource}?${params.toString()}`);

          const items = Array.isArray(data) ? data : (data.items ?? []);
          const options = items.map((it) => ({
            value: it.id,
            label:
              makeLabel?.(it) ??
              `${it.code ?? it.cod ?? it.id} • ${it.name ?? it.denumire ?? ""}`,
            __raw: it,
          }));

          return {
            options,
            hasMore: items.length === LIMIT,
            additional: { page: page + 1 },
          };
        }

        // 2) dacă CAUȚI (ex: 6022) -> ia mai multe și filtrează local
        const params = new URLSearchParams({
          skip: "0",
          limit: "2000", // ajustează dacă ai multe conturi
          sort_by: "code",
          sort_order: "asc",
        });
        const data = await authFetch(`/${resource}?${params.toString()}`);

        const items = Array.isArray(data) ? data : (data.items ?? []);
        const qq = q.toLowerCase();

        const filtered = items.filter((it) => {
          const code = String(it.code ?? it.cod ?? "").toLowerCase();
          const den = String(it.denumire ?? it.name ?? "").toLowerCase();
          return code.includes(qq) || den.includes(qq);
        });

        const slice = filtered.slice(0, LIMIT);
        const options = slice.map((it) => ({
          value: it.id,
          label:
            makeLabel?.(it) ??
            `${it.code ?? it.cod ?? it.id} • ${it.name ?? it.denumire ?? ""}`,
          __raw: it,
        }));

        return {
          options,
          hasMore: filtered.length > LIMIT,
          additional: { page: 2 },
        };
      } catch (err) {
        console.error("loadOptions error", resource, err);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [resource, makeLabel],
  );
}

export default function TipBonIesireCreate() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    denumire: "",
    contd_id: null,
    contc_id: null, // optional
  });

  // dropdown state
  const [optContD, setOptContD] = useState(null);
  const [optContC, setOptContC] = useState(null);
  const [showContD, setShowContD] = useState(false);
  const [showContC, setShowContC] = useState(false);

  const makeAccountLabel = useCallback((c) => {
    const nr =
      c.cont ?? c.number ?? c.numar ?? c.account ?? c.code ?? c.cod ?? "";
    const den = c.denumire ?? c.name ?? c.descriere ?? c.title ?? "";
    return [nr, den].filter(Boolean).join(" — ") || `Account #${c.id}`;
  }, []);

  const loadAccountOptions = useLoader("accounts", makeAccountLabel);
  const additional = useMemo(() => ({ page: 1 }), []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
            ? value === ""
              ? ""
              : Number(value)
            : value,
    }));
  };

  const onChangeContD = (opt) => {
    setOptContD(opt);
    setForm((f) => ({ ...f, contd_id: opt ? Number(opt.value) : null }));
  };
  const onChangeContC = (opt) => {
    setOptContC(opt);
    setForm((f) => ({ ...f, contc_id: opt ? Number(opt.value) : null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);

      if (!form.denumire?.trim()) throw new Error("Denumire obligatorie.");
      if (!form.contd_id) throw new Error("Cont debit (contd_id) obligatoriu.");

      const payload = {
        denumire: form.denumire.trim(),
        contd_id: Number(form.contd_id),
        contc_id: form.contc_id ? Number(form.contc_id) : null,
      };

      await authFetch("/tip_bon_iesire", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      navigate("/tip-bon-iesire/");
    } catch (err) {
      alert("Eroare la creare: " + (err?.message ?? "unknown"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="mt-4">
      <Card className="p-3">
        <h3>Creare TipBonIesire</h3>
        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Denumire</Form.Label>
                <Form.Control
                  type="text"
                  name="denumire"
                  value={form.denumire ?? ""}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            {/* Cont D (obligatoriu) */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Cont debit</Form.Label>

                <div
                  className="form-control d-flex align-items-center justify-content-between"
                  style={{ cursor: "pointer" }}
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowContD(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setShowContD(true);
                    }
                  }}
                >
                  <span className={optContD ? "" : "text-muted"}>
                    {optContD
                      ? `${optContD.code ?? optContD.cod ?? ""} • ${
                          optContD.denumire ?? optContD.name ?? ""
                        }`
                      : "Click pentru a alege cont debit…"}
                  </span>

                  {optContD ? (
                    <span
                      title="Șterge selecția"
                      style={{ padding: "0 6px" }}
                      onClick={(e) => {
                        e.stopPropagation(); // să nu deschidă modalul
                        setOptContD(null);
                        setForm((f) => ({ ...f, contd_id: null }));
                      }}
                    >
                      ✕
                    </span>
                  ) : (
                    <span className="text-muted">▾</span>
                  )}
                </div>
              </Form.Group>
            </Col>

            {/* Cont C (opțional) */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Cont credit default (opțional)</Form.Label>

                <div
                  className="form-control d-flex align-items-center justify-content-between"
                  style={{ cursor: "pointer" }}
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowContC(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setShowContC(true);
                    }
                  }}
                >
                  <span className={optContC ? "" : "text-muted"}>
                    {optContC
                      ? `${optContC.code ?? optContC.cod ?? ""} • ${
                          optContC.denumire ?? optContC.name ?? ""
                        }`
                      : "Click pentru a alege cont credit…"}
                  </span>

                  {optContC ? (
                    <span
                      title="Șterge selecția"
                      style={{ padding: "0 6px" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOptContC(null);
                        setForm((f) => ({ ...f, contc_id: null }));
                      }}
                    >
                      ✕
                    </span>
                  ) : (
                    <span className="text-muted">▾</span>
                  )}
                </div>

                <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                  La bon consum creditul real vine din stocin.bxcontd_id (via
                  bxstocin_id). contc_id poate rămâne NULL.
                </div>
              </Form.Group>
            </Col>
          </Row>

          <div className="text-end">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Se salvează…" : "Salvează"}
            </Button>
          </div>
        </Form>

        <AccountPickerModal
          show={showContD}
          onHide={() => setShowContD(false)}
          onSelect={(acc) => {
            setOptContD(acc);
            setForm((f) => ({ ...f, contd_id: acc.id }));
          }}
        />

        <AccountPickerModal
          show={showContC}
          onHide={() => setShowContC(false)}
          onSelect={(acc) => {
            setOptContC(acc);
            setForm((f) => ({ ...f, contc_id: acc.id }));
          }}
        />
      </Card>
    </Container>
  );
}
